import { Box } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

export type DataGridHeaderAlign = "left" | "center";

const STYLE_PROPS = [
  "templateColumns",
  "compact",
  "sortable",
  "sorted",
  "pinned",
  "pinnedLeftPadding",
  "align",
  "paddingLeft",
  "direction",
];

const shouldForwardProp = (prop: PropertyKey) =>
  !STYLE_PROPS.includes(String(prop));

export const Root = styled(Box, { shouldForwardProp })<{
  templateColumns: string;
  compact?: boolean;
}>(({ theme, templateColumns, compact }) => ({
  position: "sticky",
  top: 0,
  left: 0,
  zIndex: theme.zIndex.appBar,
  display: "grid",
  gridTemplateColumns: templateColumns,
  width: "100%",
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  ...(compact && {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: alpha(theme.palette.divider, 0.08),
    boxShadow: "0 6px 12px -10px rgba(11, 12, 14, 0.28)",
  }),
}));

export const PINNED_COLUMN_SHADOW = "2px 0 4px -2px rgba(0,0,0,0.21)";

export const HeaderCell = styled(Box, { shouldForwardProp })<{
  compact?: boolean;
  pinned?: boolean;
  pinnedLeftPadding?: number;
  sortable?: boolean;
  sorted?: boolean;
  align?: DataGridHeaderAlign;
  paddingLeft?: number;
}>(
  ({
    theme,
    compact,
    pinned,
    pinnedLeftPadding = 0,
    sortable,
    sorted,
    align = "center",
    paddingLeft,
  }) => ({
    height: 42,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: align === "left" ? "flex-start" : "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1.5, 0, align === "left" ? 0 : 1.5),
    position: pinned ? "sticky" : "relative",
    left: pinned ? pinnedLeftPadding : "auto",
    zIndex: pinned ? 1 : "auto",
    backgroundColor: pinned ? theme.palette.background.paper : "transparent",
    boxShadow: pinned ? PINNED_COLUMN_SHADOW : "none",
    cursor: sortable ? "pointer" : "default",
    userSelect: "none",
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    ...(compact && {
      height: 38,
      gap: "3px",
      padding: 0,
      color: theme.palette.text[sorted ? "primary" : "secondary"],
      ...(pinned && { backgroundColor: theme.palette.grey[50] }),
    }),
    ...(paddingLeft !== undefined && { paddingLeft }),
  })
);

export const HeaderLabel = styled("span", { shouldForwardProp })<{
  compact?: boolean;
}>(({ theme, compact }) => ({
  ...theme.typography.caption,
  fontWeight: 600,
  whiteSpace: "nowrap",
  ...(compact && {
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "12px",
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
  }),
}));

export const SortIndicator = styled("span", { shouldForwardProp })<{
  compact?: boolean;
  direction: "asc" | "desc" | null;
}>(({ theme, compact, direction }) => ({
  ...theme.typography.caption,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: compact || direction ? 1 : 0.4,
  ...(compact && { fontSize: 15 }),
}));
